import React, { Fragment, useReducer } from 'react';
import styled from 'styled-components';
import { filter, find } from 'ramda';
import UploadField from '~~elements/UploadField';
import Input from '~~elements/Input';
import Select from '~~elements/Select';
import Button from '~~elements/Button';
import { PlayerResource } from '~~apis/resource';
import { COLOR } from '~~elements/Const';
import { TABLET_WIDTH } from '~~elements/Const';
import { getPossibleExistPlayers } from '~~services/Player';
import PlayerDisplay from './PlayerDisplay';
import TTMultilingual from './Multilingual';
import { useTournamentState } from '../useTournament';
import { playerPositionOptions, playerHandOptions } from '../Const';
import moment from 'moment';

const CurrentAvatar = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-image: url(${({ url }) => url});
  background-size: cover;
  background-position: center;
`;

const StyledModal = styled.div`
  position: fixed;
  width: 80vw;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  background: ${COLOR.secondary};
  border-radius: 10px;
  border: 1px solid gray;
  color: white;
  z-index: 10;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  .new-player {
    &--title {
      width: 95%;
      border-bottom: 1px solid white;
      padding-bottom: 0.5rem;
      text-align: center;
      font-size: 1.2rem;
    }
    &--content {
      width: 100%;
      max-height: 50vh;
      overflow-y: auto;
      padding: 2rem 0;
      &--card {
        margin: 2rem 0;
        display: flex;
        width: 100%;
        justify-content: center;
        align-items: center;
        &-content {
          margin-left: 2rem;
        }
      }
    }
    &--control {
      margin-top: 1rem;
      display: flex;
      justify-content: center;
    }
  }

  @media (max-width: ${TABLET_WIDTH}) {
    width: 95vw;
    padding: 1rem;
    .new-player {
      &--title {
        font-size: 1rem;
      }
      &--content {
        &--card {
          flex-direction: column;
          &-content {
            margin-top: 0.5rem;
            margin-left: 0;
          }
        }
      }
      &--control {
        margin-top: 0.8rem;
      }
    }
  }
`;

const preSeasonPlayersInitialState = {
  action: '',
  players: [],
  status: [],
};
const preSeasonPlayersReducer = (state, action) => {
  switch (action.type) {
    case 'reset':
      return { ...preSeasonPlayersInitialState };
    case 'start':
      return { ...preSeasonPlayersInitialState, action: 'LOADING' };
    case 'updatePlayers':
      return {
        ...preSeasonPlayersInitialState,
        action: 'LIST',
        players: action.payload,
        status: new Array(action.payload.length).fill(''),
      };
    case 'register':
      state.status[action.payload] = 'PROCESSING';
      return { ...state };
    case 'finished': {
      state.status[action.payload] = 'FINISHED';
      const finishedPlayers = [];
      state.status.forEach((s, index) => {
        if (s !== 'FINISHED') {
          return;
        }
        finishedPlayers.push(state.players[index]);
      });

      state.players.forEach((player, index) => {
        if (state.status[index]) {
          return;
        }
        const sameNumberPlayer = find((p) => p.number === player.number, finishedPlayers);
        if (!sameNumberPlayer) {
          return;
        }

        state.status[index] = 'DUPLICATED';
      });
      return { ...state };
    }
    case 'error':
      state.status[action.payload] = 'ERROR';
      return { ...state };
    default:
      throw new Error();
  }
};

const PlayerNew = ({
  playerState,
  playerDispatch,
  players,
  getPreSeasonPlayers,
  seasonName,
  seasonUniqid,
  teamName,
  seasonTeamUniqid,
  done,
}) => {
  const { organizerUniqid } = useTournamentState();
  const [preSeasonPlayersState, preSeasonPlayersDispatch] = useReducer(
    preSeasonPlayersReducer,
    preSeasonPlayersInitialState,
  );

  const handlePreSeasonPlayers = () => {
    preSeasonPlayersDispatch({ type: 'start' });
    getPreSeasonPlayers()
      .then((prePlayers) => {
        const numberUnused = (prePlayer) => !find((p) => p.number === prePlayer.number, players);
        preSeasonPlayersDispatch({ type: 'updatePlayers', payload: filter((p) => numberUnused(p), prePlayers) });
      })
      .catch(() => {
        preSeasonPlayersDispatch({ type: 'updatePlayers', payload: [] });
      });
  };

  const handleRegisterPrePlayer = (index) => () => {
    const preSeasonPlayer = preSeasonPlayersState.players[index];
    if (!preSeasonPlayer) {
      return;
    }

    preSeasonPlayersDispatch({ type: 'register', payload: index });
    PlayerResource.createPlayerInSeasonTeamByOrganizer({
      leagueUniqid: organizerUniqid,
      seasonUniqid,
      seasonTeamUniqid,
      data: { ...preSeasonPlayer, player_uniqid: preSeasonPlayer.uniqid },
    })
      .then(() => {
        preSeasonPlayersDispatch({ type: 'finished', payload: index });
      })
      .catch(() => {
        preSeasonPlayersDispatch({ type: 'error', payload: index });
      });
  };

  const handleClosePrePlayer = () => {
    preSeasonPlayersDispatch({ type: 'reset' });
    const hasFinished = find((s) => s === 'FINISHED', preSeasonPlayersState.status);
    if (hasFinished) {
      done();
    }
  };

  const handleInputOnchange = (e) => {
    playerDispatch({ type: 'updateColumn', payload: { [e.target.name]: e.target.value } });
  };

  const handleAvatarUpdate = (data) => {
    playerDispatch({ type: 'updateColumn', payload: { avatar_url: data.url, avatar_path: data.path } });
  };

  const handleRemoveAvatar = () => {
    playerDispatch({ type: 'removeAvatar' });
  };

  const handleRegister = () => {
    playerDispatch({ type: 'updateColumn', payload: {} });
    const playerName = playerState.name.trim();
    const playerNumber = playerState.number.trim();
    if (!playerName || !playerNumber) {
      return playerDispatch({ type: 'error', payload: TTMultilingual(`ERROR.NAME_NUMBER_REQUIRED`) });
    }

    const player = find((p) => p.number === playerNumber, players);
    if (player) {
      return playerDispatch({ type: 'error', payload: TTMultilingual(`ERROR.NUMBER_DUPLICATE`) });
    }

    playerDispatch({ type: 'playerStatus', payload: 'PROCESSING' });
    getPossibleExistPlayers({ name: playerName, seasonName, teamName }).then((possiblePlayers) => {
      if (possiblePlayers.length === 0) {
        return playerRegister();
      }

      playerDispatch({ type: 'possibilityCheck', payload: possiblePlayers });
    });
  };

  const handleLinkPlayer = (playerUniqid) => () => {
    playerDispatch({ type: 'playerStatus', payload: 'PROCESSING' });
    const birthday = new Date(playerState.birthday);
    let birthdayValue = '';
    if (playerState.birthday && moment(birthday).isValid()) {
      birthdayValue = moment(birthday).format('YYYY/MM/DD');
    }
    PlayerResource.createPlayerInSeasonTeamByOrganizer({
      leagueUniqid: organizerUniqid,
      seasonUniqid,
      seasonTeamUniqid,
      data: { ...playerState, birthday: birthdayValue, player_uniqid: playerUniqid },
    })
      .then(() => {
        playerDispatch({ type: 'playerStatus', payload: 'FINISHED' });
        setTimeout(() => done(), 500);
      })
      .catch(() => {
        playerDispatch({ type: 'error', payload: TTMultilingual(`COMMON.UNKNOWN_ERROR`) });
      });
  };

  const handleLinkPlayerCancel = () => {
    playerDispatch({ type: 'playerStatus', payload: '' });
  };

  const playerRegister = () => {
    const birthday = new Date(playerState.birthday);
    let birthdayValue = '';
    if (playerState.birthday && moment(birthday).isValid()) {
      birthdayValue = moment(birthday).format('YYYY/MM/DD');
    }
    PlayerResource.createPlayerInSeasonTeamByOrganizer({
      leagueUniqid: organizerUniqid,
      seasonUniqid,
      seasonTeamUniqid,
      data: { ...playerState, birthday: birthdayValue, player_uniqid: '' },
    })
      .then(() => {
        playerDispatch({ type: 'playerStatus', payload: 'FINISHED' });
        setTimeout(() => done(), 500);
      })
      .catch(() => {
        playerDispatch({ type: 'error', payload: TTMultilingual(`COMMON.UNKNOWN_ERROR`) });
      });
  };

  const handleCancel = () => {
    playerDispatch({ type: 'reset' });
  };

  return (
    <Fragment>
      <Button mini onClick={handlePreSeasonPlayers}>
        {TTMultilingual(`PRE_TOURN_RECORDED_PLAYERS`)}
      </Button>
      <div className="registration--item-center">- or -</div>
      {playerState.avatar_url ? (
        <div className="registration--item-avatar">
          <CurrentAvatar url={playerState.avatar_url}></CurrentAvatar>
          <UploadField
            styleType="box"
            url="/api/media"
            customBtn={(onClick) => (
              <Button width="unset" color="white" onClick={onClick}>
                {TTMultilingual(`FORM.REPLACE`)}
              </Button>
            )}
            onChange={handleAvatarUpdate}
          />
          <Button width="unset" color="red" onClick={handleRemoveAvatar} mini>
            {TTMultilingual(`COMMON.REMOVE`)}
          </Button>
        </div>
      ) : (
        <div className="registration--item-avatar default">
          <UploadField
            styleType="box"
            url="/api/media"
            customBtn={(onClick) => (
              <Button width="unset" color="white" onClick={onClick}>
                {TTMultilingual(`FORM.UPLOAD`)}
              </Button>
            )}
            onChange={handleAvatarUpdate}
          />
        </div>
      )}
      <div>
        {TTMultilingual(`COLUMN.NAME`)}：
        <Input width="100%" name="name" onChange={handleInputOnchange} value={playerState.name} />
      </div>
      <div>
        {TTMultilingual(`COLUMN.NUMBER`)}：
        <Input width="100%" name="number" onChange={handleInputOnchange} value={playerState.number} />
      </div>
      <div>
        {TTMultilingual(`COLUMN.BIRTHDAY`)}：
        <Input width="100%" name="birthday" type="date" onChange={handleInputOnchange} value={playerState.birthday} />
      </div>
      <div>
        {TTMultilingual(`COLUMN.FIRST_POSITION`)}：
        <Select width="100%" name="first_position" onChange={handleInputOnchange} value={playerState.first_position}>
          {playerPositionOptions.map((key) => (
            <option key={key} value={key}>
              {TTMultilingual(`POSITION.${key}`)}
            </option>
          ))}
        </Select>
      </div>
      <div>
        {TTMultilingual(`COLUMN.SECOND_POSITION`)}：
        <Select width="100%" name="second_position" onChange={handleInputOnchange} value={playerState.second_position}>
          {playerPositionOptions.map((key) => (
            <option key={key} value={key}>
              {TTMultilingual(`POSITION.${key}`)}
            </option>
          ))}
        </Select>
      </div>
      <div>
        {TTMultilingual(`COLUMN.P_HAND`)}：
        <Select width="100%" name="p_hand" onChange={handleInputOnchange} value={playerState.p_hand}>
          {playerHandOptions.map((key) => (
            <option key={key} value={key}>
              {TTMultilingual(`HAND.${key}`)}
            </option>
          ))}
        </Select>
      </div>
      <div>
        {TTMultilingual(`COLUMN.B_HAND`)}：
        <Select width="100%" name="b_hand" onChange={handleInputOnchange} value={playerState.b_hand}>
          {playerHandOptions.map((key) => (
            <option key={key} value={key}>
              {TTMultilingual(`HAND.${key}`)}
            </option>
          ))}
        </Select>
      </div>
      {playerState.playerStatus === 'ERROR' && playerState.errorMsg && (
        <div className="registration--error-msg">{playerState.errorMsg}</div>
      )}
      {playerState.playerStatus === 'FINISHED' && (
        <div className="registration--success-msg">{TTMultilingual(`SUCCESS.REGISTER`)}</div>
      )}
      {playerState.playerStatus === 'PROCESSING' && (
        <div className="registration--discribe-msg">{TTMultilingual(`COMMON.PROCESSING`)}</div>
      )}
      <div className="registration--item-control">
        <Button
          width="unset"
          nowrap
          color="blue"
          mini
          onClick={handleRegister}
          disabled={playerState.playerStatus === 'PROCESSING'}
        >
          {TTMultilingual(`FORM.REGISTER`)}
        </Button>
        <Button width="unset" nowrap color="red" mini onClick={handleCancel}>
          {TTMultilingual(`COMMON.CANCEL`)}
        </Button>
      </div>
      {playerState.playerStatus === 'PLAYER_CHOICE' && (
        <StyledModal>
          <div className="new-player--title">{TTMultilingual(`RECORD_CONNECTION`)}</div>
          <div>{TTMultilingual(`RECORD_CONNECTION_DESCRIPTION`)}</div>
          <div className="new-player--content">
            {playerState.possiblePlayers.map((player, index) => (
              <div className="new-player--content--card" key={index}>
                <div className="registration--item">
                  <PlayerDisplay editable={false} player={{ ...player, number: '' }} handlePlayerDelete={null} />
                </div>
                <div className="new-player--content--card-content">
                  {TTMultilingual(`RECENT_RECORD`)}：
                  <br />
                  {player.experiences.map((experience, experienceIndex) => (
                    <Fragment key={experienceIndex}>
                      {experience.season}
                      <br />
                      {`${experience.team} #${experience.number}`}
                      <br />
                      <br />
                    </Fragment>
                  ))}
                  <Button onClick={handleLinkPlayer(player.uniqid)} mini>
                    {TTMultilingual(`LINK_THEN_REGISTRY`)}
                  </Button>
                </div>
              </div>
            ))}
            <div className="new-player--content--card">
              <Button onClick={handleLinkPlayer('')} width="10rem">
                {TTMultilingual(`SYSTEM_AUTO_DETERMINE`)}
              </Button>
            </div>
            <div className="new-player--content--card">
              <Button onClick={handleLinkPlayerCancel} width="10rem" color="white">
                {TTMultilingual(`COMMON.CANCEL`)}
              </Button>
            </div>
          </div>
        </StyledModal>
      )}
      {preSeasonPlayersState.action && (
        <StyledModal>
          <div className="new-player--title">{TTMultilingual(`PRE_TOURN_RECORDED_PLAYERS`)}</div>
          {preSeasonPlayersState.action === 'LOADING' ? (
            <div>{TTMultilingual(`COMMON.LOADING`)}</div>
          ) : (
            preSeasonPlayersState.players.length === 0 && <div>{TTMultilingual(`NO_RECORD`)}</div>
          )}
          <div className="new-player--content">
            <div className="registration--list">
              {preSeasonPlayersState.players.map((prePlayer, index) => (
                <div className="registration--item" key={index}>
                  <PlayerDisplay editable={false} player={prePlayer} handlePlayerDelete={null} />
                  <div className="registration--item-control">
                    {!preSeasonPlayersState.status[index] ? (
                      <Button width="unset" color="blue" nowrap mini inverse onClick={handleRegisterPrePlayer(index)}>
                        {TTMultilingual(`FORM.REGISTER`)}
                      </Button>
                    ) : (
                      <Button width="unset" color="white" nowrap mini disabled>
                        {preSeasonPlayersState.status[index] === 'PROCESSING'
                          ? TTMultilingual(`COMMON.PROCESSING`)
                          : preSeasonPlayersState.status[index] === 'FINISHED'
                          ? TTMultilingual(`SUCCESS.REGISTER`)
                          : preSeasonPlayersState.status[index] === 'DUPLICATED'
                          ? TTMultilingual(`ERROR.NUMBER_DUPLICATE`)
                          : TTMultilingual(`COMMON.UNKNOWN_ERROR`)}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="new-player--control">
            <Button
              width="unset"
              color="white"
              onClick={handleClosePrePlayer}
              disabled={filter((s) => s === 'PROCESSING', preSeasonPlayersState.status).length > 0}
            >
              {TTMultilingual(`COMMON.CLOSE`)}
            </Button>
          </div>
        </StyledModal>
      )}
    </Fragment>
  );
};

export default PlayerNew;
